const IDLE_TIMEOUT: number = 3000;

class PreloadEntryHandler {
  // private manager: RemoteModuleManager;
  private taskQueue: Array<() => void>;

  private entryPreloadLock = true;
  private entryPreloadLockObserver: MutationObserver | null = null;
  private entryPreloadStartTs: number;

  private MAX_PRELOAD_TIME = 30 * 1000;

  constructor(taskQueue: Array<() => void>) {
    this.taskQueue = taskQueue;
    this.initEntryPreloadLockObserver();
  }

  private getIsTimeout = () => {
    // 未开始 preload 的情况
    if (!this.entryPreloadStartTs) {
      return false;
    }
    return Date.now() - this.entryPreloadStartTs >= this.MAX_PRELOAD_TIME;
  };

  // 预加载 entry js
  idlePreloadEntry = (): Promise<boolean> =>
    new Promise((resolve, reject) => {
      // 防止预加载逻辑重复调用
      if (this.entryPreloadStartTs) {
        return;
      }
      this.entryPreloadStartTs = Date.now();

      try {
        const processTaskQueue = (deadline: IdleDeadline) => {
          // 预加载逻辑超过时间限制，停止运行，防止永远停不下来
          if (this.getIsTimeout()) {
            reject(new Error('[PreloadEntryHandler Error]: preload entry timeout'));
            return;
          }
          while (this.taskQueue.length > 0 && !this.entryPreloadLock) {
            // 如果当前帧还有足够的时间，或者队列中的任务是紧急的
            if (deadline.timeRemaining() > 0 || deadline.didTimeout) {
              // 从队列中取出第一个任务并执行
              this.taskQueue.shift()?.();
            } else {
              // 如果时间不够了，中断执行，等待下一个requestIdleCallback
              break;
            }
          }
          // 如果任务不能执行或队列中还有任务未完成，就在下一个requestIdleCallback再次尝试
          if (this.entryPreloadLock || this.taskQueue.length > 0) {
            requestIdleCallback(processTaskQueue, { timeout: IDLE_TIMEOUT });
          }
          if (!this.taskQueue.length) {
            resolve(true);
            this.disconnectObserver();
          }
        };
        requestIdleCallback(processTaskQueue, { timeout: IDLE_TIMEOUT });
      } catch (err) {
        this.entryPreloadLock = true;
        reject(new Error(`[PreloadEntryHandler Error]: ${err.message || err}`));
      }
    });

  // 预加载 chunk js
  initEntryPreloadLockObserver = () => {
    try {
      let debounceUnlockPreloadTimer: null | number = null;
      /*
          取一个稍微不那么激进的时长（BOE 环境下接口可能慢一些）
        */
      const DELAY_TIMING = 1500;
      const [headElement] = document.getElementsByTagName('head');
      this.entryPreloadLockObserver = new MutationObserver(mutations => {
        if (this.getIsTimeout()) {
          // 超出预加载时间，断开监听
          this.disconnectObserver();
        }
        const isResourceLoading = mutations.some(mutation =>
          Array.from(mutation.addedNodes).some(node => {
            if (node.nodeName === 'LINK') {
              return (
                (node as HTMLLinkElement).getAttribute('rel') === 'stylesheet' &&
                Boolean((node as HTMLLinkElement).getAttribute('href'))
              );
            }
            return (
              node.nodeName === 'SCRIPT' && Boolean((node as HTMLScriptElement).getAttribute('src'))
            );
          }),
        );
        if (isResourceLoading) {
          this.entryPreloadLock = true;
          if (debounceUnlockPreloadTimer !== null) {
            clearTimeout(debounceUnlockPreloadTimer);
            debounceUnlockPreloadTimer = null;
          }
          debounceUnlockPreloadTimer = window.setTimeout(() => {
            this.entryPreloadLock = false;
          }, DELAY_TIMING);
        }
      });
      this.entryPreloadLockObserver.observe(headElement, {
        childList: true,
      });
      setTimeout(() => {
        this.disconnectObserver();
      }, this.MAX_PRELOAD_TIME);
    } catch {
      this.entryPreloadLock = true;
    }
  };

  private disconnectObserver = () => {
    if (!this.entryPreloadLockObserver) {
      return;
    }
    this.entryPreloadLockObserver.disconnect();
    this.entryPreloadLockObserver = null;
  };
}

export default PreloadEntryHandler;
